import {
  Box,
  Flex,
  HStack,
  Avatar,
  Heading,
  Text,
  Icon,
  Tag,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Card,
  CardBody,
  Portal,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { FiMail, FiCalendar, FiSettings, FiEdit, FiLogOut } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { User } from '@supabase/supabase-js';
import { useAtomValue } from 'jotai';
import { isPremiumAtom } from '../../../atoms/v0/subscriptionAtoms';
import { forceSignOut } from '../../../utils/authUtils';
import { useNavigate } from 'react-router-dom';

interface UserProfileCardProps {
  user: User | null;
  onSignOut: () => Promise<void>;
  cardBg: string;
  borderColor: string;
  accentColor: string;
}

export const UserProfileCard = ({ user, onSignOut, cardBg, borderColor, accentColor }: UserProfileCardProps) => {
  const { t } = useTranslation(['v0/common', 'v0/me']);
  const today = new Date();
  const isPremium = useAtomValue(isPremiumAtom);
  const navigate = useNavigate();
  const toast = useToast();

  // -- Updated Premium Badge Styles --
  const premiumColor = useColorModeValue('white', 'gray.900'); // Text color for premium badge
  const premiumBg = useColorModeValue('teal.400', 'teal.300'); // Background for premium badge

  // -- Non-Premium Badge Styles --
  const grayBadgeColor = useColorModeValue('gray.600', 'gray.400');
  const grayBadgeBg = useColorModeValue('gray.100', 'gray.700');

  // 增强的登出处理函数
  const handleSignOut = async () => {
    try {
      // 尝试使用传入的 onSignOut 函数
      await onSignOut();
    } catch (error) {
      console.error('使用传入的 onSignOut 函数失败，尝试强制登出:', error);
      // 如果失败，使用强制登出函数
      await forceSignOut(
        navigate,
        toast,
        t('v0/common:actions.logout'),
        t('v0/common:errors.logoutFailed', '退出失败')
      );
    }
  };

  return (
    <Card
      bg={cardBg}
      borderRadius="xl"
      mb={4}
      boxShadow="sm"
      overflow="hidden"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <CardBody p={4}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
        >
          <HStack spacing={3}>
            <Avatar
              size="md"
              name={user?.email || 'User'}
              bg={accentColor}
              boxShadow="sm"
            />
            <Box>
              <HStack align="center" spacing={2}>
                <Heading size="sm" fontWeight="600">{user?.email?.split('v0/@')[0] || t('common:user')}</Heading>
                <Badge
                  px={2.5} // Slightly more padding
                  py={0.5}
                  borderRadius="full"
                  fontSize="xs" // Slightly larger font
                  textTransform="uppercase"
                  fontWeight="600" // Slightly bolder
                  color={isPremium ? premiumColor : grayBadgeColor}
                  bg={isPremium ? premiumBg : grayBadgeBg}
                  // Removed border for a cleaner look
                  // borderWidth={isPremium ? '1px' : '0px'}
                  // borderColor={isPremium ? premiumColor : 'transparent'}
                  variant={isPremium ? 'solid' : 'subtle'} // Use solid variant for premium
                >
                  {t('v0/common:subscription.plus')}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                <Icon as={FiMail} boxSize={3} mr={1} display="inline" />
                {user?.email}
              </Text>
            </Box>
          </HStack>

          <HStack spacing={3}>
            <Tag size="sm" variant="subtle" colorScheme="teal">
              <Icon as={FiCalendar} mr={1} />
              <Text fontSize="xs">{format(today, 'yyyy')}</Text>
            </Tag>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<FiSettings size={16} />}
                variant="ghost"
                size="sm"
              />
              <Portal>
                <MenuList>
                  <MenuItem icon={<FiEdit />}>{t('v0/me:editProfile')}</MenuItem>
                  <MenuItem icon={<FiLogOut />} onClick={handleSignOut}>{t('v0/common:actions.logout')}</MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </Flex>
      </CardBody>
    </Card>
  );
};